import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";
import { WA_LINK, EASE } from "@/components/Reveal";

const LINKS = [
  { to: "/restaurants", label: "Restaurants", testid: "nav-link-restaurants" },
  { to: "/cloud-kitchen", label: "Cloud Kitchen", testid: "nav-link-cloud-kitchen" },
  { to: "/hospital-canteen", label: "Hospitals", testid: "nav-link-hospitals" },
  { to: "/investors", label: "Investors", testid: "nav-link-investors" },
  { to: "/franchisee", label: "Franchisee", testid: "nav-link-franchisee" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "border-b border-white/5 bg-obsidian/80 backdrop-blur-xl" : "border-b border-transparent bg-obsidian/40 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link data-testid="nav-logo" to="/" onClick={() => setOpen(false)} className="flex items-center">
          <img src="/images/tasteride-logo.png" alt="Taste Ride" className="h-6 w-auto sm:h-7" />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `group relative font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
                  isActive ? "text-saffron" : "text-slate-300 hover:text-saffron"
                }`
              }
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-saffron transition-[width] duration-300 group-hover:w-full" />
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-whatsapp-button"
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full bg-wa px-5 py-2.5 font-display text-sm font-semibold text-obsidian transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(37,211,102,0.35)] sm:flex"
          >
            <MessageCircle size={16} strokeWidth={2.5} />
            WhatsApp Us
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-cream lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-b border-white/5 bg-obsidian/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 font-display text-lg font-medium transition-colors ${
                      isActive ? "bg-white/5 text-saffron" : "text-cream hover:bg-white/5 hover:text-saffron"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                data-testid="nav-mobile-whatsapp"
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-wa px-5 py-3 font-display text-sm font-semibold text-obsidian"
              >
                <MessageCircle size={16} strokeWidth={2.5} />
                WhatsApp Us
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
